import {Injectable} from '@angular/core';
import {AngularFirestore, AngularFirestoreDocument} from '@angular/fire/compat/firestore';
import {AngularFireStorage} from '@angular/fire/compat/storage';
import {Agenda} from '../models/agenda';
import {firstValueFrom, Observable} from 'rxjs';
import {SnackBarIconService} from '../shared/snack-bar-icon/snack-bar-icon.service';
import {Event, Activity, Day, Topic} from '../models/event';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {ConfirmComponent} from '../shared/dialogs/confirm/confirm.component';
import {Role} from '../models/role';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {RoleService} from './role.service';
import {Invite} from '../models/invite';


@Injectable({
  providedIn: 'root'
})
export class AgendaService {

  public roles: Role[] = []
  public members: any[] = []

  public agendaActivityNotes: Agenda[] = []
  public agendaActivityDataSource: MatTableDataSource<any>;

  public agendaTopicNotes: Agenda[] = []
  public agendaTopicDataSource: MatTableDataSource<any>;

  public objectImage: any = null; // store input file for avatar
  public storedImage: any; // store url image for local preview

  private itemDoc: AngularFirestoreDocument<Agenda>;
  private item: Observable<Agenda>;

  public arrayMemberAgenda: Agenda[];

  public arrayMembersToSend: any[] = [];

  public arrayFullMembersAgenda: any[] = [];

  public arrayAllMemberAgenda: any[] = [];
  public arrayEvents: any[] = []

  public spinner: boolean;

  public invitesDataSource: MatTableDataSource<any>;
  public invitesDisplay2Columns: string[] = [ 'member_name', 'date_sent', 'accept', 'delete', 'select', 'edit' ];

  constructor (
    private _roleService: RoleService,
    private db: AngularFirestore,
    private fs: AngularFireStorage,
    private fb: FormBuilder,
    private snackBar: SnackBarIconService,
    private dialog: MatDialog) { }

  loadRoles () {

    this._roleService.select()
      .subscribe((result) => {
        this.roles = []
        result.forEach((role) => {
          this.roles.push({
            "id": role.payload.doc.id,
            "name": role.payload.doc.data().name
          })
        })
      })
  }

  createRoleForm () {
    let form: FormGroup = this.fb.group({
      id: new FormControl(''),
      role_id: new FormControl(''),
      member: new FormControl(''),
      requireConfirm: new FormControl(''),
      requireSchedule: new FormControl(''),
    });

    return form
  }

  select () {
    return this.db.collection<Agenda>('sbccv_agenda', ref => ref.orderBy('day_date', 'asc')).snapshotChanges();
  }

  selectByEvent (event_id) {
    return this.db.collection<Agenda>('sbccv_agenda', ref => ref
      .where('event_id', '==', event_id)).snapshotChanges();
  }

  async selectByOneEvent (event_id: string): Promise<any[]> {
    const querySnapshot = await firstValueFrom(
      this.db.collection<Agenda>('sbccv_agenda', ref => ref
        .where('event_id', '==', event_id)).get()
    );

    return querySnapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    }));
  }

  async selectByEvents (events: string[]): Promise<any[]> {
    // console.log("getProductsByEvents: " + JSON.stringify(events))
    const array: any[] = [];

    for (const event_id of events) {
      try {
        const querySnapshot = await this.db.collection<Agenda>('sbccv_agenda', ref => ref
          .where('event_id', '==', event_id))
          .get()
          .toPromise();

        querySnapshot.forEach(doc => {
          // console.log("doc.id: " + doc.id)
          const data: any = {
            "id": doc.id,
            ...doc.data()
          }
          array.push(data);
        });

      } catch (error) {
        console.error(`Erro ao buscar agenda para o evento ${ event_id }:`, error);
      }
    }
    return array;
  }

  selectById (id) {
    this.itemDoc = this.db.doc<Agenda>('sbccv_agenda/' + id);
    this.item = this.itemDoc.valueChanges();
    return this.item;
  }

  makeNote (data): Agenda {
    let agenda = new Agenda();

    agenda.id = data.id;

    agenda.requireConfirm = data.data().requireConfirm;
    agenda.requireSchedule = data.data().requireSchedule;

    agenda.dateConfirmed = data.data().dateConfirmed;
    agenda.scheduled_date = data.data().scheduled_date;
    agenda.scheduled_hour = data.data().scheduled_hour

    agenda.member_id = data.data().member_id;
    agenda.member_name = data.data().member_name;
    agenda.member_state = data.data().member_state;
    agenda.member_country = data.data().member_country;

    agenda.event_id = data.data().event_id;
    agenda.event_name = data.data().event_name;

    agenda.day_id = data.data().day_id;
    agenda.day_date = data.data().day_date;
    agenda.day_name = data.data().day_name;

    agenda.depto_id = data.data().depto_id;
    agenda.depto_name = data.data().depto_name;

    agenda.activity_id = data.data().activity_id;
    agenda.activity_name = data.data().activity_name;
    agenda.activity_startTime = data.data().activity_startTime;

    agenda.level = data.data().level;

    if (agenda.level == "t") {
      agenda.topic_id = data.data().topic_id;
      agenda.topic_name = data.data().topic_name;
      agenda.topic_startTime = data.data().topic_startTime;
      agenda.topic_duration = data.data().topic_duration;
    }

    agenda.role_id = data.data().role_id;
    agenda.role_name = data.data().role_name;

    return agenda;

  }

  loadAgendaTopic (topicId: any) {
    this.agendaTopicNotes = []
    return this.db.collection<Agenda>('sbccv_agenda', ref =>
      ref.where('topic_id', '==', topicId)
        .where('level', '==', 't')
    ).snapshotChanges();
  }

  loadAgendaActivity (activityId: any) {
    this.agendaActivityNotes = []
    return this.db.collection<Agenda>('sbccv_agenda', ref =>
      ref.where('activity_id', '==', activityId)
        .where('level', '==', 'a')
    ).snapshotChanges();
  }


  save (entity: Agenda) {

    let id = entity.id;
    delete entity.id;

    if (id) {
      delete entity.member_id
      delete entity.member_name
    }

    return this.db.collection('sbccv_agenda').doc(id).set(Object.assign({}, entity), {'merge': true})
      .then((ret) => {
        this.snackBar.openSnackBar("Sucesso!", '', 'success');
      })
      .catch((error) => {
        console.log("erro: " + JSON.stringify(error))
        this.snackBar.openSnackBar("Erro", '', 'error');
      })
  }

  update (entity: Agenda) {

    let id = entity.id;

    delete entity.id;
    delete entity.member_id
    delete entity.member_name

    return this.db.collection('sbccv_agenda').doc(id).set(Object.assign({}, entity), {'merge': true})
      .then((ret) => {
        this.snackBar.openSnackBar("Sucesso!", '', 'success');
      })
      .catch((error) => {
        console.log("erro: " + JSON.stringify(error))
        this.snackBar.openSnackBar("Erro", '', 'error');
      })
  }

  insert (entity: Agenda) {
    //console.log("insert entity: " + JSON.stringify(entity))
    delete entity.id;
    return this.db.collection('sbccv_agenda').add(Object.assign({}, entity))
      .then((ret) => {
        this.snackBar.openSnackBar("Sucesso!", '', 'success');
      })
      .catch((error) => {
        console.log("erro: " + JSON.stringify(error))
        this.snackBar.openSnackBar("Erro", '', 'error');
      })
  }

  removeAgenda (element) {
    let dialogRef = this.dialog.open(ConfirmComponent, {
      width: '350px',
      height: '270px',
      panelClass: 'custom-dialog-container',
      data: {title: "Confirme", message: element.member_name + "<br><br>Excluir este " + element.role_name}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result == "ok") {

        this.db.collection('sbccv_agenda').doc(element.id).delete()
          .then(
            () => {
              this.snackBar.openSnackBar("Sucesso!", '', 'success')
            }
          )
          .catch(
            (error) => {console.log("delete room error: " + error.message)}
          )
      }
    })
  }

  editAgenda (element) {
    console.log(JSON.stringify(element))

    let entity: any = {
      'day_date': element.day_date,
      'day_name': element.day_name,
      'activity_name': element.activity_name,
      'activity_startTime': element.activity_startTime,
      'topic_name': element.topic_name,
      'topic_startTime': element.topic_startTime
    }

    return this.db.collection('sbccv_agenda').doc(element.id)
      .set(Object.assign({}, entity), {merge: true});

  }

  createAgenda (
    level: any,
    data: any,
    event: Event,
    day: Day,
    activity: Activity,
    topic: Topic = null) {

    let agenda = new Agenda();

    agenda.id = data.id;

    agenda.level = level;

    agenda.member_id = data.member_id;
    agenda.member_name = data.member_name;
    agenda.member_state = data.member_state ? data.member_state : "";;
    agenda.member_country = data.member_country ? data.member_country : "";

    agenda.role_id = data.role_id;
    agenda.role_name = data.role_name;

    agenda.requireConfirm = data.requireConfirm ? true : false;
    agenda.requireSchedule = data.requireSchedule ? true : false;

    agenda.event_id = event.id
    agenda.event_name = event.name

    agenda.day_id = day.id
    agenda.day_date = day.date
    agenda.day_name = day.name

    agenda.depto_id = activity.depto_id
    agenda.depto_name = activity.depto

    agenda.activity_id = activity.id
    agenda.activity_name = activity.name
    agenda.activity_startTime = activity.startTime

    if (level == "t") {
      agenda.topic_id = topic.id
      agenda.topic_name = topic.name
      agenda.topic_startTime = topic.startTime
      agenda.topic_duration = topic.duration
    }

    return agenda
  }


  loadAgenda (event_id: any) {
    return this.db.collection<Agenda>('sbccv_agenda', ref =>
      ref.where('event_id', '==', event_id)
        .orderBy('member_name', 'asc')
    ).snapshotChanges()
  }

  resetAgendaForInvites () {
    this.invitesDataSource = new MatTableDataSource([]);
  }

  loadAgendaForInvites (event_id: any, letter_id: any) {
    return this.db.collection<Agenda>('sbccv_agenda', ref =>
      ref.where('event_id', '==', event_id)
        .orderBy('member_name', 'asc')).snapshotChanges()
      .subscribe((result) => {

        let arrayAgenda: any[] = result.map(item => {

          let agenda: any = {} // = new Agenda();
          agenda.id = item.payload.doc.id;
          agenda.member_id = item.payload.doc.data().member_id;
          agenda.member_name = item.payload.doc.data().member_name;

          agenda.event_id = item.payload.doc.data().event_id;
          agenda.event_name = item.payload.doc.data().event_name;
          agenda.day_id = item.payload.doc.data().day_id;

          let dt: any = item.payload.doc.data().day_date;
          agenda.day_date = new Date(dt.seconds * 1000);

          agenda.day_name = item.payload.doc.data().day_name;

          agenda.activity_id = item.payload.doc.data().activity_id;
          agenda.activity_name = item.payload.doc.data().activity_name;
          agenda.activity_startTime = item.payload.doc.data().activity_startTime;

          agenda.level = item.payload.doc.data().level;

          if (agenda.level == "t") {
            agenda.topic_id = item.payload.doc.data().topic_id;
            agenda.topic_name = item.payload.doc.data().topic_name;
            agenda.topic_startTime = item.payload.doc.data().topic_startTime;
            agenda.topic_duration = item.payload.doc.data().topic_duration;
          }

          agenda.role_id = item.payload.doc.data().role_id;
          agenda.role_name = item.payload.doc.data().role_name;

          return agenda;

        })

        arrayAgenda.forEach((ag) => {

          if (ag.level == "a") {
            //atividades
            let activities: any = arrayAgenda.filter((e) => {
              return e.activity_id == ag.activity_id
            })

            //equipe da atividade
            ag.activityStaff = []
            activities.forEach(el => {
              if (el.level == 'a') {
                ag.activityStaff.push({
                  'role_name': el.role_name,
                  'member_name': el.member_name
                })
              }
            });

            //tópicos da atividade
            ag.activityTopics = []
            activities.forEach(el => {
              if (el.topic_name) {
                ag.activityTopics.push({
                  'topic_startTime': el.topic_startTime,
                  'topic_name': el.topic_name
                })
              }
            });
            ag.activityTopics.sort(this.compareTimes)

          } else if (ag.level == "t") {
            //tópicos
            let topics: any = arrayAgenda.filter((e) => {
              return e.topic_id == ag.topic_id
            })
            //equipe do tópico
            ag.topicStaff = []
            topics.forEach(el => {
              if (el.level == 't') {
                ag.topicStaff.push({
                  'role_name': el.role_name,
                  'member_name': el.member_name
                })
              }
            });

          }
        })

        //TEMOS OS DADOS DA AGENDA
        //PREPARA OS COVITES E VERIFICA QUEM JA FOI CONVIDADO
        this.checkInvites(arrayAgenda, event_id, letter_id)

      })

  }

  checkInvites (arrayAgenda, event_id, letter_id) {

    let invites: any[] = []

    this.db.collection<Invite>('sbccv_invites', ref => ref
      .where('event_id', '==', event_id)
      .where('letter_id', '==', letter_id)).snapshotChanges()
      .subscribe((result) => {

        result.forEach((item) => {
          // CAPTURA DATA DE ENVIO E DATA DE RESPOSTA
          let dt_sent: any = item.payload.doc.data().date_sent
          let dt_response: any = item.payload.doc.data().date_response

          //PREPARA O ITEM SER APRESRENTADO NA LISTA
          let entity = {
            'id': item.payload.doc.id,
            'event_id': item.payload.doc.data().event_id,
            'letter_id': item.payload.doc.data().letter_id,
            'member_id': item.payload.doc.data().member_id,
            'accept': item.payload.doc.data().accept,
            'date_sent': dt_sent ? new Date(dt_sent.seconds * 1000) : "",
            'date_response': dt_response ? new Date(dt_response.seconds * 1000) : "",
            'obs': item.payload.doc.data().obs,
            // 'type': item.payload.doc.data().type,
            'url_back': item.payload.doc.data().url_back
          }
          //COLECIONA O ITEM
          invites.push(entity)
        })

        //PREPARA A TABELA A SER APRESENTADA
        this.makeTable(invites, arrayAgenda)
      })
  }

  makeTable (invites, arrayAgenda) {

    let arrayInvite: any[] = [];
    let current_member: string = ""
    let members: any[] = [];

    arrayAgenda.forEach(element => {

      if (current_member != element.member_name) {
        current_member = element.member_name
        members.push({
          'member_id': element.member_id,
          'member_name': element.member_name,
          'member_email': element.member_email
        })
      }

    });

    members.forEach((item) => {

      let agenda: any[] = arrayAgenda.filter((e) => {
        return e.member_id == item.member_id
      })

      agenda.sort(this.compareAppointments)

      let inviteExist = invites.find((e) => {
        return e.member_id == item.member_id
      })

      let flag: boolean = false
      if (inviteExist) {
        flag = true
        // console.log("inviteExist.member_name: " + JSON.stringify(inviteExist))
      }

      //   console.log("inviteExist: " + JSON.stringify(inviteExist))

      arrayInvite.push({
        'id': inviteExist ? inviteExist.id : "",
        'date': new Date(),
        'member_id': item.member_id,
        'member_name': item.member_name,
        'member_email': item.member_email,
        'agenda': agenda,
        'selected': false,
        'sent': flag,
        'date_sent': inviteExist ? inviteExist.date_sent : "",
        'accept': inviteExist ? inviteExist.accept : false,
        'date_response': inviteExist ? inviteExist.date_response : "",
        'obs': inviteExist ? inviteExist.obs : "",
        'type': inviteExist ? inviteExist.type : "0",
        "url_back": inviteExist ? inviteExist.url_back : ""
      })

    })

    this.invitesDataSource = new MatTableDataSource(arrayInvite);

  }

  compareTimes (a, b) {
    let pa: string = a.topic_startTime;
    let pb: string = b.topic_startTime;
    if (pa > pb) return 1;
    if (pb > pa) return -1;
    return 0;
  }

  compareAppointments (a, b) {
    let dt_a: Date = new Date(a.day_date)
    let dt_b: Date = new Date(b.day_date)

    const pa = dt_a.getTime().toString() + a.activity_startTime + a.topic_startTime
    const pb = dt_b.getTime().toString() + b.activity_startTime + b.topic_startTime

    if (pa > pb) return 1;
    if (pb > pa) return -1;
    return 0;
  }


  loadMemberAgenda (member_id: any) {
    return this.db.collection<Agenda>('sbccv_agenda', ref => ref
      .where('member_id', '==', member_id)
      .where("level", "==", "t")
    ).snapshotChanges();
  }

  loadMemberAgendaByEvent (member_id: any, event_id: any) {
    return this.db.collection<Agenda>('sbccv_agenda', ref => ref
      .where('member_id', '==', member_id)
      .where('event_id', '==', event_id)
      .where("level", "==", "t")
    ).snapshotChanges();
  }


  loadAllMemberAgendaByEvent (member_id: any, event_id: any) {
    return this.db.collection<Agenda>('sbccv_agenda', ref => ref
      .where('member_id', '==', member_id)
      .where('event_id', '==', event_id)
      //.orderBy('day_date', 'desc')
    ).snapshotChanges();
  }






  //////////////////////////////////////////////////////
  getAgendaByMember (member_id: any) {
    //    console.log("member_id: " + member_id)

    this.arrayEvents = []
    this.arrayAllMemberAgenda = []

    return this.db.collection<Agenda>('sbccv_agenda', ref => ref
      .where('member_id', '==', member_id)
      //.orderBy('event_name')
    ).snapshotChanges()
      .subscribe((result) => {

        result.forEach((item) => {

          let agenda: any = {
            'id': item.payload.doc.id,
            'event_name': item.payload.doc.data().event_name,
            'event_id': item.payload.doc.data().event_id,
            'member_name': item.payload.doc.data().member_name,
            'member_id': item.payload.doc.data().member_id,
            'role_name': item.payload.doc.data().role_name,
            'role_id': item.payload.doc.data().role_id,
            'day_id': item.payload.doc.data().day_id,
            'day_name': item.payload.doc.data().day_name,
            'day_date': item.payload.doc.data().day_date,

            'activity_id': item.payload.doc.data().activity_id,
            'activity_name': item.payload.doc.data().activity_name,
            'activity_startTime': item.payload.doc.data().activity_startTime,

            'topic_id': item.payload.doc.data().topic_id,
            'topic_name': item.payload.doc.data().topic_name,
            'topic_startTime': item.payload.doc.data().topic_startTime,
            'topic_duration': item.payload.doc.data().topic_duration,

            'level': item.payload.doc.data().level,

            'selected': false

          };


          this.arrayAllMemberAgenda.push(agenda)
        })

        //  console.log("--------------------------------------------------------")
        //  console.log("arrayAllMemberAgenda: " + JSON.stringify(this.arrayAllMemberAgenda))
        //  console.log("--------------------------------------------------")


        let _event: any = {}
        //   let count: number = 0;

        this.arrayAllMemberAgenda.forEach((item) => {

          //    count += 1

          //     console.log("count: " + count)

          _event = this.arrayEvents.find((e) => {
            return e.id == item.event_id
          })

          if (!_event) { // se não encontrou evento, add
            _event = this.createEvent(item)
            this.arrayEvents.push(_event)
          }

          this.arrayEvents.sort(this.compareNames)
          //     console.log("event.name: " + _event.name)

          // dias
          let _day: any = _event.days.find((d) => {
            return d.id == item.day_id
          })

          if (!_day) { // se não encontrou, adiciona
            _day = this.createDay(item)
            _event.days.push(_day)
          }

          _event.days.sort(this.compareDays)
          //    console.log("_day.name: " + _day.name)

          // atividades
          let _activity: any = _day.activities.find((d) => {
            return d.id == item.activity_id
          })

          if (!_activity) { // se não encontrou, adiciona
            _activity = this.createActivity(item)
            _day.activities.push(_activity)
          }

          _day.activities.sort(this.compareStart)
          //     console.log("_activity.name: " + _activity.name)

          // topics
          let _topic: any = _activity.topics.find((d) => {
            return d.id == item.topic_id
          })

          if (!_topic) { // se não encontrou, adiciona
            _topic = this.createTopic(item)
            _activity.topics.push(_topic)
          }

          _activity.topics.sort(this.compareStart)
          //    console.log("_topic.name: " + _topic.name)

        })

        //      console.log("events: " + JSON.stringify(this.arrayEvents))

      },
        (error) => {
          console.log(JSON.stringify(error))
          this.snackBar.openSnackBar("Error load letter roles", '', 'error');
        })
  }

  createEvent (item) {
    let evt: any = {}
    evt.id = item.event_id;
    evt.name = item.event_name;
    evt.days = [];
    return evt;
  }

  createDay (item) {
    let day: any = {};
    day.id = item.day_id;
    day.name = item.day_name;
    day.date = item.day_date;
    day.activities = [];
    return day;
  }

  createActivity (item) {
    let activity: any = {};
    activity.id = item.activity_id;
    activity.name = item.activity_name;
    activity.startTime = item.activity_startTime
    activity.topics = [];
    return activity;
  }

  createTopic (item) {
    let topic: any = {};
    topic.id = item.topic_id;
    topic.name = item.topic_name;
    topic.startTime = item.topic_startTime
    return topic;
  }

  compareNames (a, b) {
    if (a.name > b.name) return 1;
    if (b.name > a.name) return -1;
    return 0;
  }

  compareDays (a, b) {
    if (a.date > b.date) return 1;
    if (b.date > a.date) return -1;
    return 0;
  }

  compareStart (a, b) {

    const pa = String(a.startTime)
    const pb = String(b.startTime)

    if (pa > pb) return 1;
    if (pb > pa) return -1;

    return 0;
  }

  getAgendaByActivityAndDepto (activity_id, depto_id) {
    return this.db.collection<Agenda>('sbccv_agenda', ref => ref
      .where('activity_id', '==', activity_id)
      .where('depto_id', '==', depto_id)
    ).get();
  }

  makeMsgError (errorsCode) {
    let msgError: string
    switch (errorsCode) {
      case "auth/email-already-exists":
        msgError = "Este email já está em uso"
        break;
    }
    return msgError
  }

}
